// Mirrors Rubble.Core.Entities / Rubble.Core.Models.Part in the Admin API. The C# enums are
// annotated with JsonStringEnumConverter, so these string unions match the wire format exactly.

export type TitleType = "Cosmetic" | "Mechanical";

export type TitleFieldWeight =
  | "Excluded"
  | "Low"
  | "Medium"
  | "High"
  | "Mandatory";

export type TitleField =
  | "Make"
  | "Model"
  | "MkSeries"
  | "ShortModel"
  | "Year"
  | "CategoryName"
  | "CategoryAlias"
  | "PrimaryKeyword"
  | "Placement"
  | "Aspects"
  | "PartNumber"
  | "Synonyms"
  | "Colour"
  | "PaintCode"
  | "BodyStyle"
  | "Doors"
  | "FuelType"
  | "EngineCapacity"
  | "EngineCode"
  | "Bhp"
  | "Gears"
  | "Transmission"
  | "Mileage"
  | "TagNumber";

export type TitleRemovalStep =
  | "RemovePaintCode"
  | "RemoveDoors"
  | "RemoveBodyStyle"
  | "RemoveColour"
  | "RemoveLowestSynonym"
  | "RemoveLowestAspect"
  | "ShortenPlacementWording"
  | "RemovePrimaryKeyword"
  | "ShortenYear"
  | "RemoveMkSeries"
  | "RemoveShortModel"
  | "RemoveCategoryAlias"
  | "RemoveTagNumber"
  | "RemoveMileage"
  | "RemoveBhp"
  | "RemoveGears"
  | "RemoveTransmission"
  | "RemoveFuelType"
  | "RemoveEngineCapacity"
  | "RemoveEngineCode"
  | "RemovePartNumber";

export interface ITitleFieldPriority {
  field: TitleField;
  weight: TitleFieldWeight;
}

/**
 * One row of the aspect priority list. Aspects are eBay item specifics, so they are named rather
 * than enumerated, and each carries its own weight - marking "Mirror Adjustment Method" mandatory
 * keeps Powerfold or Manual in the title even when Aspects as a whole is only medium priority.
 */
export interface ITitleAspectPriority {
  name: string;
  weight: TitleFieldWeight;
}

/** One eBay item aspect offered by the picker, from the eBay Taxonomy API. */
export interface IEbayAspectOption {
  name: string;
  /** eBay requires this aspect on listings in the category. */
  isRequired: boolean;
  /** eBay's own recommended values, e.g. Powerfold and Manual. */
  values: string[];
  /** How many searches eBay attributes to the aspect. */
  searchCount: number;
}

export interface IEbayAspectsResult {
  ebayCategoryId: string | null;
  aspects: IEbayAspectOption[];
}

export interface ITitleConfigRequest {
  isActive: boolean;
  titleType: TitleType;
  marketplacePrimaryKeyword: string | null;
  synonyms: string | null;
  titleCharacterLimit: number;
  placementSource: string | null;
  aspectsSource: string | null;
  partNumberSource: string | null;
  /** Ask the AI for Driver/Passenger side wording instead of Right/Left. */
  preferDriverPassenger: boolean;
  fieldPriorities: ITitleFieldPriority[];
  removalOrder: TitleRemovalStep[];
  aspectPriorities: ITitleAspectPriority[];
  /** Free-text rules for this category, appended to the compiled title prompt. */
  additionalInstructions: string | null;
  useCategoryDamagePrompt: boolean;
  includeStandardConditionRules: boolean;
  damagePrompt: string | null;
  /**
   * Lives on the part category rather than the configuration row, but is editable here because
   * this is the only screen that needs it - it is what the eBay aspect picker looks up.
   */
  ebayCategoryId: string | null;
}

export interface ITitleConfigResponse extends ITitleConfigRequest {
  id: string | null;
  partCategoryId: string;
  categoryName: string;
  compiledSystemPrompt: string | null;
  compiledDamagePrompt: string | null;
  /** False when the category has no saved row and these are seeded defaults. */
  isSaved: boolean;
}

export interface ITitleConfigCategorySummary {
  partCategoryId: string;
  name: string;
  titleType: TitleType;
  isActive: boolean;
  isConfigured: boolean;
}

export interface ITitlePreviewExampleData {
  make: string;
  model: string;
  shortModel: string;
  mk: string;
  series: string;
  year: string;
  categoryName: string;
  categoryAlias: string;
  placement: string;
  aspects: string;
  partNumber: string;
  colour: string;
  paintCode: string;
  bodyStyle: string;
  doors: string;
  fuelType: string;
  engineCapacity: string;
  engineCode: string;
  bhp: string;
  gears: string;
  transmission: string;
  mileage: string;
  tagNumber: string;
}

export interface ITitlePreviewExclusion {
  field: string;
  reason: string;
}

export interface ITitlePreviewResult {
  title: string;
  characterCount: number;
  characterLimit: number;
  score: number;
  includedFields: string[];
  excludedFields: ITitlePreviewExclusion[];
  warnings: string[];
  compiledSystemPrompt: string | null;
}

export interface IDamagePreviewResult {
  conditionDescription: string;
  observations: string[];
  warnings: string[];
  compiledDamagePrompt: string | null;
}

export const TITLE_FIELD_LABELS: Record<TitleField, string> = {
  Make: "Make",
  Model: "Model",
  MkSeries: "MK / Series",
  ShortModel: "Short Model",
  Year: "Year",
  CategoryName: "Category Name",
  CategoryAlias: "Category Alias",
  PrimaryKeyword: "Primary Keyword",
  Placement: "Placement (TBC)",
  Aspects: "Aspects",
  PartNumber: "Part Number",
  Synonyms: "Synonyms",
  Colour: "Colour",
  PaintCode: "Colour Code / Paint Code",
  BodyStyle: "Body Style",
  Doors: "Door",
  FuelType: "Fuel Type",
  EngineCapacity: "Engine Capacity",
  EngineCode: "Engine Code",
  Bhp: "BHP",
  Gears: "No. Gears",
  Transmission: "Transmission",
  Mileage: "Mileage",
  TagNumber: "Tag Number",
};

export const TITLE_REMOVAL_STEP_LABELS: Record<TitleRemovalStep, string> = {
  RemovePaintCode: "Paint Code",
  RemoveDoors: "Door",
  RemoveBodyStyle: "Body Style",
  RemoveColour: "Colour",
  RemoveLowestSynonym: "Synonyms (last to first)",
  RemoveLowestAspect: "Lowest priority Aspect",
  ShortenPlacementWording: "Passenger/Driver wording (keep N/S or O/S)",
  RemovePrimaryKeyword: "Primary Keyword",
  ShortenYear: "Shorten Year (e.g. 2008-2012 to 08-12)",
  RemoveMkSeries: "MK / Series",
  RemoveShortModel: "Short Model",
  RemoveCategoryAlias: "Category Alias",
  RemoveTagNumber: "Tag Number",
  RemoveMileage: "Mileage",
  RemoveBhp: "BHP",
  RemoveGears: "No. Gears",
  RemoveTransmission: "Transmission",
  RemoveFuelType: "Fuel Type",
  RemoveEngineCapacity: "Engine Capacity",
  RemoveEngineCode: "Engine Code",
  RemovePartNumber: "Part Number",
};

export const ALL_TITLE_FIELDS = Object.keys(TITLE_FIELD_LABELS) as TitleField[];

export const ALL_REMOVAL_STEPS = Object.keys(
  TITLE_REMOVAL_STEP_LABELS,
) as TitleRemovalStep[];

export const TITLE_FIELD_WEIGHTS: TitleFieldWeight[] = [
  "Mandatory",
  "High",
  "Medium",
  "Low",
  "Excluded",
];

/**
 * Placement has no dedicated field yet - it is still derived from the Side and Position enums on
 * the part and its category, which is why the field is labelled "(TBC)" on the screen.
 */
export const PLACEMENT_SOURCES = [
  { value: "SideAndPosition", label: "Side + Position (derived)" },
  { value: "PartOverrideOnly", label: "Part override only" },
  { value: "CategoryDefaultOnly", label: "Category default only" },
  { value: "None", label: "None" },
];

export const ASPECTS_SOURCES = [
  { value: "Aspects", label: "Aspects" },
  { value: "None", label: "None" },
];

export const PART_NUMBER_SOURCES = [
  { value: "PartNumber", label: "Part Number" },
  { value: "OemPartNumber", label: "OEM Part Number" },
  { value: "EitherPartNumber", label: "Either, part number first" },
  { value: "None", label: "None" },
];

/** Matches the Door Wing Mirror worked example in the handover spec. */
export const DEFAULT_EXAMPLE_DATA: ITitlePreviewExampleData = {
  make: "Ford",
  model: "Focus",
  shortModel: "Focus",
  mk: "MK3",
  series: "",
  year: "2011-2014",
  categoryName: "",
  categoryAlias: "",
  placement: "N/S Passenger Left",
  // Name: value pairs, so the preview can exercise the per-aspect priorities - an aspect is
  // ranked by its name but it is the value that lands in the title.
  aspects: "Mirror Adjustment Method: Powerfold, Mirror Glass Type: Heated",
  partNumber: "8PE0941003BC",
  colour: "Black",
  paintCode: "G1",
  bodyStyle: "Hatchback",
  doors: "5 Door",
  fuelType: "Diesel",
  engineCapacity: "2.0",
  engineCode: "DW10",
  bhp: "150 BHP",
  gears: "6 Speed",
  transmission: "Manual",
  mileage: "62,000 Miles",
  tagNumber: "GP12345",
};
